import { Backdrop, Box, Fade, Typography, alpha } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { DialogCloseButton } from './DialogCloseButton';

export type DropZoneProps = {
  maxFileCount?: number;
  onDrop: (files: File[]) => Promise<void>;
  onMaxFileCountExceeded?: (maxFileCount: number) => void;
};

export function DropZone(props: DropZoneProps) {
  const { t } = useTranslation();
  const [lastTarget, setLastTarget] = useState<EventTarget | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    noClick: true,
    maxFiles: props.maxFileCount || 0,
  });

  function handleDrop(files: File[]) {
    if (props.maxFileCount && files.length >= props.maxFileCount) {
      props.onMaxFileCountExceeded?.(props.maxFileCount);
    } else {
      props.onDrop(files);
    }

    setLastTarget(null);
    setIsOpen(false);
  }

  function handleDragLeave() {
    setLastTarget(null);
    setIsOpen(false);
  }

  useEffect(() => {
    window.addEventListener('dragenter', (e) => {
      if (e.dataTransfer?.types.includes('Files')) {
        setLastTarget(e.target);
        setIsOpen(true);
      }
    });

    window.addEventListener('dragleave', (e) => {
      if (e.target === lastTarget) {
        handleDragLeave();
      }
    });
  }, []);

  return (
    <Backdrop
      open={isOpen}
      invisible
      sx={{
        zIndex: (theme) => theme.zIndex.drawer - 2,
      }}
    >
      <Fade in={isOpen}>
        <Box
          {...getRootProps()}
          sx={(theme) => ({
            inset: '0',
            background: alpha(theme.palette.background.paper, 0.5),
            position: 'absolute',
          })}
        >
          <Box
            onDragLeave={handleDragLeave}
            sx={(theme) => ({
              height: '100%',
              width: '100%',
              padding: theme.spacing(5),
            })}
          >
            <Box
              sx={(theme) => ({
                height: '100%',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `3px dashed ${theme.palette.secondary.main}`,
                background: alpha(theme.palette.background.paper, 0.5),
                backdropFilter: 'blur(20px)',
                borderRadius: theme.shape.borderRadius,
                boxShadow: theme.shadows[24],
                pointerEvents: 'none',
              })}
            >
              <input {...getInputProps()} />
              <Typography variant="h1">{t('dropFilesHere', 'Drop files here')}</Typography>
              <DialogCloseButton
                sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, pointerEvents: 'auto' }}
                onClose={() => setIsOpen(false)}
              />
            </Box>
          </Box>
        </Box>
      </Fade>
    </Backdrop>
  );
}
